import { supabase } from "@/lib";
import memoryService from "@/services/memoryService";
import { useAuthStore } from "@/store";
import { useCallback, useEffect, useRef, useState } from "react";

// Module-level cache to persist across component remounts
const workspaceCache = {
  loadedWorkspaceId: null,
  isLoading: false,
};

export const useFileMention = (currentWorkspaceId, toast) => {
  const [mentionDropdownOpen, setMentionDropdownOpen] = useState(false);
  const [mentionTriggerPos, setMentionTriggerPos] = useState(null);
  const [mentionSearchTerm, setMentionSearchTerm] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [workspaceFiles, setWorkspaceFiles] = useState([]);
  const [workspaceMemories, setWorkspaceMemories] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const mentionStartRef = useRef(null);
  // Note: We use module-level cache instead of refs to survive component remounts

  const userId = useAuthStore((state) => state.user?.id);

  const loadWorkspaceFilesAndMemories = useCallback(async () => {
    try {
      // Load workspace files from Supabase
      const { data: fileData, error: fileError } = await supabase
        .from("workspace_files")
        .select("*")
        .eq("workspace_id", currentWorkspaceId)
        .order("created_at", { ascending: false })
        .limit(10);

      if (fileError) throw fileError;

      // Format files for display
      const formattedFiles =
        fileData?.map((file) => ({
          id: file.id,
          name: file.file_name,
          size: file.file_size,
          type: file.file_type,
          updated_at: file.updated_at,
          storage_path: file.storage_path,
          file_path: file.file_path,
          itemType: "file",
        })) || [];

      setWorkspaceFiles(formattedFiles);

      // Load workspace memories from Mem0 via memoryService
      if (userId) {
        const memoryData = await memoryService.getWorkspaceMemories(
          userId,
          currentWorkspaceId
        );

        // Format memories for display (Mem0 uses 'memory' field for content)
        const formattedMemories =
          memoryData?.slice(0, 10).map((memory) => ({
            id: memory.id,
            name:
              memory.metadata?.title ||
              memory.memory?.substring(0, 50) ||
              "Untitled Memory",
            content: memory.memory || memory.content,
            updated_at: memory.updated_at || memory.metadata?.created_at,
            itemType: "memory",
          })) || [];

        setWorkspaceMemories(formattedMemories);
      }
    } catch (error) {
      console.error("Error loading workspace data:", error);
      if (toast) {
        toast({
          title: "Error",
          description: "Failed to load workspace files and memories",
          variant: "destructive",
        });
      }
    }
  }, [currentWorkspaceId, userId, toast]);

  // Load workspace files and memories when workspace changes
  useEffect(() => {
    // Skip if already loaded this workspace or currently loading (using module-level cache)
    if (
      workspaceCache.isLoading ||
      workspaceCache.loadedWorkspaceId === currentWorkspaceId
    ) {
      return;
    }

    if (currentWorkspaceId) {
      workspaceCache.isLoading = true;
      workspaceCache.loadedWorkspaceId = currentWorkspaceId;

      loadWorkspaceFilesAndMemories().finally(() => {
        workspaceCache.isLoading = false;
      });
    } else {
      // Clear data when no workspace
      setWorkspaceFiles([]);
      setWorkspaceMemories([]);
      workspaceCache.isLoading = false;
      workspaceCache.loadedWorkspaceId = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId]); // Only depend on currentWorkspaceId, not the function

  // Close mention dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicked element is in the dropdown
      const dropdownEl = document.querySelector(".file-mention-dropdown");

      // Only close if click is not in dropdown AND not in textarea
      const textareaEl = document.querySelector("textarea");
      if (
        mentionDropdownOpen &&
        !textareaEl?.contains(event.target) &&
        !(dropdownEl && dropdownEl.contains(event.target))
      ) {
        setMentionDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mentionDropdownOpen]);

  // Handle mention detection in text
  const handleMentionDetection = useCallback(
    (message, caretPos) => {
      const textBeforeCaret = message.slice(0, caretPos);

      // Also detect standalone @ character
      const hasAtSymbol = textBeforeCaret.endsWith("@");
      const mentionMatch = textBeforeCaret.match(/@([^@\s]*)$/);

      if (mentionMatch || hasAtSymbol) {
        const searchTerm = mentionMatch ? mentionMatch[1].toLowerCase() : "";
        setMentionSearchTerm(searchTerm);

        // Filter both files and memories based on search term
        // For empty search term, show all items
        const filteredFiles = searchTerm
          ? workspaceFiles.filter((file) =>
              file.name.toLowerCase().includes(searchTerm)
            )
          : workspaceFiles;

        const filteredMemories = searchTerm
          ? workspaceMemories.filter((memory) =>
              memory.name.toLowerCase().includes(searchTerm)
            )
          : workspaceMemories;

        // Combine results
        setFilteredItems([...filteredFiles, ...filteredMemories]);

        mentionStartRef.current = hasAtSymbol
          ? caretPos
          : caretPos - mentionMatch[0].length;
        setSelectedIndex(0); // Reset selection on dropdown open

        return true; // Mention detected
      } else {
        setMentionDropdownOpen(false);
        mentionStartRef.current = null;
        return false; // No mention detected
      }
    },
    [workspaceFiles, workspaceMemories]
  );

  // Force trigger mention dropdown
  const forceTriggerMentionDropdown = useCallback(() => {
    // Make sure we have data first
    if (workspaceFiles.length === 0 && workspaceMemories.length === 0) {
      loadWorkspaceFilesAndMemories();
    }

    // Show all available items
    setFilteredItems([...workspaceFiles, ...workspaceMemories]);

    // Set dropdown state to open
    setMentionDropdownOpen(true);

    // Set selection to first item
    setSelectedIndex(0);
  }, [workspaceFiles, workspaceMemories, loadWorkspaceFilesAndMemories]);

  // Handle keyboard navigation for mention dropdown
  const handleMentionKeyDown = useCallback(
    (e, filteredItems, onSelect) => {
      if (mentionDropdownOpen && filteredItems.length > 0) {
        // Arrow up
        if (e.key === "ArrowUp") {
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev === 0 ? filteredItems.length - 1 : prev - 1
          );
          return true;
        }
        // Arrow down
        else if (e.key === "ArrowDown") {
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev === filteredItems.length - 1 ? 0 : prev + 1
          );
          return true;
        }
        // Enter to select
        else if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          const selectedItem = filteredItems[selectedIndex];

          if (selectedItem) {
            onSelect(selectedItem);
          } else {
            console.error("No item found at index", selectedIndex);
          }
          return true;
        }
        // Escape to close dropdown
        else if (e.key === "Escape") {
          e.preventDefault();
          setMentionDropdownOpen(false);
          return true;
        }
        // Tab to select and close
        else if (e.key === "Tab") {
          e.preventDefault();
          const selectedItem = filteredItems[selectedIndex];
          if (selectedItem) {
            onSelect(selectedItem);
          }
          return true;
        }
      }
      return false;
    },
    [mentionDropdownOpen, selectedIndex]
  );

  return {
    mentionDropdownOpen,
    setMentionDropdownOpen,
    mentionTriggerPos,
    setMentionTriggerPos,
    mentionSearchTerm,
    setMentionSearchTerm,
    filteredItems,
    setFilteredItems,
    workspaceFiles,
    workspaceMemories,
    selectedIndex,
    setSelectedIndex,
    mentionStartRef,
    loadWorkspaceFilesAndMemories,
    handleMentionDetection,
    forceTriggerMentionDropdown,
    handleMentionKeyDown,
  };
};
